import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {DatePipe} from '@angular/common';

export enum BusinessObjectsIds {
    orderBaseShipmentUnit = 'orderBase.shipmentUnit',
    orderBaseRefNums = 'orderBase.refNums'
}

export enum BookingTypes {
    fcl = 'orderTypeGid eq "TW.FE" or  orderTypeGid  eq "TW.FI"',
    lcl = 'orderTypeGid eq "TW.GE" or  orderTypeGid  eq "TW.GI"',
    air = 'orderTypeGid eq "TW.AE" or  orderTypeGid  eq "TW.AI"',
    transport = 'orderTypeGid eq "TW.CT"',
    customClearance = 'orderTypeGid eq "TW.CJ"',
    others = ''
}


const commonRoutes = {
    getApiByApiName: (apiName: string) => `base-api/${apiName}`,
    getNestedData: (businessObjectId: any, link: any) => `link-api?businessObjectId=${businessObjectId}&links=${link}`,
    getBookingsByFilter: (page: number, size: number) => `v1/bookings-by-filter?page=${page}&size=${size}`,
    getBookingOrderTypes: () => `v1/booking-order-types`,
    getShipmentRoles: () => `v1/shipment-roles`,
    getDomains: () => `v1/booking-domains`,
    getCurrencyCode: () => `v1/currency-codes`,
};

const invoiceRoutes = {
    getFilteredInvoices: (page: number, size: number) => `v1/invoices?page=${page}&size=${size}`,
    getSelectedInvoice: () => `v1/invoice-documents`,
    getInvoicesByBookingId: (page: number, size: number) => `v1/invoices-by-bookingId?page=${page}&size=${size}`,
    getFilteredStatements: (page: number, size: number) => `v1/soa?page=${page}&size=${size}`,
    getSelectedStatement: () => `v1/soa-documents`,
    getSoaCountStatistics: () => `v1/count-statistics`,
    // = ${bookingId} bookingId
};

const shipmentDetailRoutes = {
    getShipmentDetails: () => `v1/shipment-details-by-booking-id-and-bl`,
    getBlNumberByBookingId: () => `v1/booking/bl-numbers`,
    getTrackingByBookingId: () => `v1/shipment-tracking`,
};
const notificationRoutes = {
    getPagedNotificationByStatus: (page: number, size: number) => `v1/notifications?page=${page}&size=${size}`,
    getCountOfUnreadNotifications: () => `v1/notification-unread-count`,
    updateNotificationAsRead: (markAllAsRead: boolean) => `v1/notifications?markAllAsRead=${markAllAsRead}`
};

const documentsRoute = {
    getDocumentByDocumentGid: () => `v1/document-content`,

};

const publicRoutes = {
    getPublicTrackingByTrackingId: () => `v1/public-shipment-tracking`
};

@Injectable({
    providedIn: 'root'
})

export class CommonService {

    constructor(private httpClient: HttpClient) {
    }

    getApiByApiName(apiName: string): Observable<any> {

        const $apiData: Observable<any> = this.httpClient
            .get(commonRoutes.getApiByApiName(apiName), {
                params: {
                    limit: '50'
                }
            })
            .pipe(
                map((x: any) => {
                    const orderBases: any = {
                        // totalTraffic: x.items === [] ? x.items : [],
                        items: x.items ? x.items : [],
                    };
                    return orderBases;
                }));
        return $apiData;
    }

    getNestedData(businessObjectId: BusinessObjectsIds, link: string): Observable<any> {
        const $data: Observable<any> = this.httpClient
            .get(commonRoutes.getNestedData(businessObjectId, link))
            .pipe(
                map((x) => {
                    return x;
                })
            );
        return $data;
    }

    getShipmentDetails(bookingId: string, blNumber: string) {
        // @ts-ignore
        const $shipmentDetail: Observable<any> = this.httpClient
            .get(shipmentDetailRoutes.getShipmentDetails(), {
                params: {
                    bookingId: bookingId,
                    blNumber: blNumber,
                },
                responseType: 'text'
            })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        // of(
        // DataBuilder.buildActualData(shipmentType));
        return $shipmentDetail;
    }

    getTrackingByBookingId(trackingId: string) {
        const $tracking: Observable<any> = this.httpClient
            .get(shipmentDetailRoutes.getTrackingByBookingId(), {
                params: {
                    trackingId
                }
            }).pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $tracking;
    }

    getCurrencyCode() {
        const currencyCode: Observable<any> = this.httpClient
            .get(commonRoutes.getCurrencyCode())
            .pipe(
                map((x) => {
                        return x as any;
                    }
                )
            );
        return currencyCode;
    }

    updateNotificationAsRead(markAllAsRead = true) {
        const $updateNotification: Observable<any> = this.httpClient
            .put(notificationRoutes.updateNotificationAsRead(markAllAsRead), {})
            .pipe(
                map((x) => {
                    return x as any;
                })
            );
        return $updateNotification;
    }


    getCountOfUnreadNotifications() {
        const $notificationCount: Observable<any> = this.httpClient
            .get(notificationRoutes.getCountOfUnreadNotifications())
            .pipe(
                map((x) => {
                    return x as any;
                })
            );
        return $notificationCount;
    }

    getPagedNotificationByStatus(page: number, size: number, statusFilter: string) {
        // @ts-ignore
        const $notification: Observable<any> = this.httpClient
            .get(notificationRoutes.getPagedNotificationByStatus(page, size), {
                params: {
                    statusFilter,
                }
            }).pipe(
                map((x) => {
                    return x as any;
                })
            );
        return $notification;
    }


    getBlNumbersByBookingId(bookingId: string) {
        const $blNumbers: Observable<any> = this.httpClient
            .get(shipmentDetailRoutes.getBlNumberByBookingId(), {
                params: {
                    bookingId
                }
            }).pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $blNumbers;
    }

    getBookingsByFilter(filterObj: any, page: number, size: number) {
        if (filterObj === {} || filterObj === null || !filterObj) {
            filterObj = {
                activeOnly: true,
                filterByIdentifier: null,
                orderTypes: null,
                loadPort: null,
                dischargePort: null,
                customerReferenceNumber: null,
                fromDate: null,
                toDate: null,
                submittedSI: false,
                draftSI: false,
            };
        }
        const datePipe = new DatePipe('en-US');
        filterObj.fromDate = filterObj?.fromDate?.toString()?.length > 0 ?
            datePipe.transform(filterObj.fromDate, 'yyyy-MM-dd\'T\'HH:mm:ss') : '';
        filterObj.toDate = filterObj?.toDate?.toString()?.length > 0 ?
            datePipe.transform(filterObj.toDate, 'yyyy-MM-dd\'T\'HH:mm:ss') : '';
        const q = JSON.stringify(filterObj);
        const $bookings: Observable<any> = this.httpClient
            .get(commonRoutes.getBookingsByFilter(page, size), {
                params: {
                    q,
                }
            })
            .pipe(
                map(
                    (x) => {
                        return x;
                    }
                )
            );
        return $bookings;
    }

    getInvoicesByBookingId(bookingId: any, page: number, size: number) {
        const $invoices: Observable<any> = this.httpClient
            .get(invoiceRoutes.getInvoicesByBookingId(page, size), {
                params: {
                    bookingId
                }
            })
            .pipe(
                map(
                    (x: any) => {
                        return x as any;
                    }
                )
            );
        return $invoices;
    }

    getFilteredInvoices(filterOptions: any, page: number, size: number) {
        if (filterOptions === {} || filterOptions === null || !filterOptions) {
            filterOptions = {
                filterByIdentifier: null,
                orderTypes: null,
                bookingId: null,
                customerReferenceNumber: null,
                fromDate: null,
                toDate: null,
            };
        }
        const datePipe = new DatePipe('en-US');
        filterOptions.fromDate = filterOptions?.fromDate?.toString()?.length > 0 ?
            datePipe.transform(filterOptions.fromDate, 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'') : '';
        filterOptions.toDate = filterOptions?.toDate?.toString()?.length > 0 ?
            datePipe.transform(filterOptions.toDate, 'yyyy-MM-dd\'T\'HH:mm:ss\'Z\'') : '';
        const q = JSON.stringify(filterOptions);
        const $filterInvoices: Observable<any> = this.httpClient
            .get(invoiceRoutes.getFilteredInvoices(page, size), {
                params: {
                    q
                }
            })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $filterInvoices;
    }

    getBookingOrderTypes() {
        const $orderType: Observable<any> = this.httpClient
            .get(commonRoutes.getBookingOrderTypes())
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $orderType;
    }

    getFilteredStatements(page: any, size: any, filterObj?: any) {
        if (!filterObj) {
            filterObj = {
                filterByIdentifier: null,
                unPaidBoolean: false,
                partiallyPaidBoolean: false,
                overDueBoolean: false,
                currencyCode: '',
            };
        }
        const q = JSON.stringify(filterObj);
        const $statements: Observable<any> = this.httpClient
            .get(invoiceRoutes.getFilteredStatements(page, size), {
                params: {
                    q
                }
            })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $statements;
    }

    getShipmentRoles() {
        const $shipmentRoles: Observable<any> = this.httpClient
            .get(commonRoutes.getShipmentRoles())
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $shipmentRoles;
    }

    getDomains() {
        const $domains: Observable<any> = this.httpClient
            .get(commonRoutes.getDomains())
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $domains;
    }


    getInvoiceDocuments(invoiceGid: string) {
        const $invoice: Observable<any> = this.httpClient
            .get(invoiceRoutes
                .getSelectedInvoice(), {
                params: {
                    invoiceGid
                }
            })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $invoice;
    }

    getStatementDocuments(invoiceGid: string) {
        const $statement: Observable<any> = this.httpClient
            .get(
                invoiceRoutes.getSelectedStatement(), {
                    params: {
                        invoiceGid
                    }
                }
            ).pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $statement;
    }

    getDocumentByDocumentGid(documentGid: string) {
        const $document: Observable<any> = this.httpClient
            .get(documentsRoute.getDocumentByDocumentGid(), {
                params: {
                    'document-gid': documentGid
                },
                responseType: 'blob'
            }).pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $document;
    }

    getSoaCountStatistics() {
        const $countStats: Observable<any> = this.httpClient
            .get(invoiceRoutes.getSoaCountStatistics()).pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $countStats;
    }

    getPublicTrackingByTrackingId(trackingId: any) {
        const $publicTracking: Observable<any> = this.httpClient
            .get(publicRoutes.getPublicTrackingByTrackingId(), {
                params: {
                    trackingId
                }
            })
            .pipe(
                map(
                    (x) => {
                        return x as any;
                    }
                )
            );
        return $publicTracking;
    }

}
